import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// `withCredentials: true` lets the browser carry the HttpOnly `bakery_session`
// cookie set by /api/auth/login — no Bearer token in localStorage.
export const api = axios.create({ baseURL: API, withCredentials: true });

const CSRF_COOKIE = "bakery_csrf";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

function readCookie(name) {
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

// Double-submit CSRF token: the backend sets `bakery_csrf` (non-HttpOnly) at
// login so we can echo it back as a header on state-changing requests.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // A 401 from the supervisor-pin verification endpoint just means
    // "wrong PIN" — don't kill the actual session over it.
    const url = err?.config?.url || "";
    const isPinCheck = url.includes("/auth/verify-supervisor-pin");
    if (err?.response?.status === 401 && !isPinCheck) {
      // Clear UI-only cached user metadata (the real session is the cookie).
      localStorage.removeItem("bakery_user");
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Helper for raw `fetch` calls (e.g. binary downloads like CSV/ZIP) that need
 * the same cookie-auth + CSRF behaviour as the axios client.
 */
export function authFetch(url, opts = {}) {
  const method = (opts.method || "GET").toUpperCase();
  const headers = new Headers(opts.headers || {});
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  return fetch(url, { ...opts, credentials: "include", headers });
}
